import React, { Suspense, useEffect } from 'react';

import { fg } from '@confluence/feature-gating';
import { Attribution } from '@confluence/error-boundary/entry-points/Attribution.ts';
import { TransparentErrorBoundary } from '@confluence/error-boundary/entry-points/TransparentErrorBoundary.ts';

import { useConfluenceAutomationNudgeActions } from '../hooks/useConfluenceAutomationNudgeActions.tsx';

import {
	clearAutomationNudgeActions,
	publishAutomationNudgeActions,
} from './AutomationNudgeActionsStore.ts';

const AutomationNudgeActionsResolver = ({ contentId }: { contentId: string }): null => {
	const actions = useConfluenceAutomationNudgeActions(contentId);

	useEffect(() => {
		publishAutomationNudgeActions(actions);
	}, [actions]);

	useEffect(() => clearAutomationNudgeActions, []);

	return null;
};

/**
 * Resolves the automation discovery nudge actions inside their own error and suspense boundaries so a
 * failure or suspension only affects these nudges instead of the whole Rovo config provider.
 */
export const AutomationNudgeActionsLoader = ({
	contentId,
}: {
	contentId: string;
}): React.JSX.Element | null => {
	if (!fg('cc_automations_nudge_actions_error_boundary')) {
		return null;
	}

	return (
		<TransparentErrorBoundary key={contentId} attribution={Attribution.AUTOMATION_FOR_CONFLUENCE}>
			<Suspense fallback={null}>
				<AutomationNudgeActionsResolver contentId={contentId} />
			</Suspense>
		</TransparentErrorBoundary>
	);
};
